/**
 * ConfirmDialog.tsx — the shared centred confirmation modal (G2 clinical governance).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: G2 (confirmations + audit).
 *
 * WHY: every irreversible clinical/destructive action (approve, reject, issue
 * prescription, charge the consult fee, delete a user, revoke an invite) must
 * pause on an explicit confirmation — and the reject/record flows must capture
 * a REASON that lands in the server-side audit log. This primitive was
 * extracted (and upgraded) from the private GatedDialog that lived inside
 * admin/callbacks/SwimlaneBoard.tsx, so the whole portal shares ONE modal
 * grammar instead of each surface re-deriving overlay/Escape/focus handling.
 *
 * A11y mirrors the Drawer (ui/kit.tsx): `role="dialog" aria-modal`, the title
 * is bound via `useId` + aria-labelledby, focus moves INTO the dialog on open
 * (the reason field when present, else the panel) and returns to the opener on
 * close; Escape and an overlay click both cancel. By default there is no full
 * focus trap, matching the Drawer's documented trade-off: a staff dialog's Tab
 * leaving the panel lands on the page it was opened from, and nothing there acts
 * on a single key. `trapFocus` opts a dialog in where that is not true (below).
 *
 * Two exports:
 *   - {@link DialogShell} — the bare centred modal frame (a11y included) for
 *     flows that bring their own form/buttons (e.g. the Close-ticket flow).
 *   - {@link ConfirmDialog} — the confirm/cancel dialog with the optional
 *     reason capture: `requireReason` gates confirm behind ≥3 trimmed chars;
 *     `optionalReason` shows the field without gating (empty → undefined).
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { containTab } from './focusables';
import { Button, Field, TextArea } from './kit';

/** Compact (non-full-width) chrome for the dialog's action buttons. */
const dialogButtonStyle = { width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' } as const;

/**
 * The bare centred modal frame: overlay + Escape cancel, focus-in on open and
 * focus return on close. A child that autofocused itself (e.g. the reason
 * field) keeps focus — the panel is only focused when nothing inside it took
 * it. Callers render it only while open (no `open` prop — mount = open).
 */
export function DialogShell({
  title,
  onCancel,
  width = 'min(440px, 100%)',
  className,
  trapFocus = false,
  children,
}: {
  title: string;
  onCancel: () => void;
  /** Panel width. The confirm default suits a sentence + two buttons; a flow
   *  hosting a real form (the deal drawer's Log-activity composer, 2026-08-27)
   *  passes a wider clamp so the editor toolbar never wraps into two rows. */
  width?: string;
  /**
   * A class on the PANEL, for a caller that styles its own dialog in CSS (the
   * journey's "Why we ask", 2026-09-24). Additive: unset renders no class
   * attribute at all, so every other dialog is byte-identical. It cannot move
   * the overlay — that stays the shared fixed layer — and a caller must never
   * give the panel a lingering transform or filter (the `position: fixed`
   * rules in tokens.css).
   */
  className?: string;
  /**
   * KEEP TAB INSIDE THE PANEL (review, 2026-09-25). Opt-in, default off, so every
   * dialog that does not pass it behaves exactly as before. With it, Tab from the
   * last control wraps to the first and Shift+Tab from the first wraps to the
   * last (`ui/focusables.ts`, the celebration's containment).
   *
   * WHO NEEDS IT: a dialog opened over controls that act on ONE key. The
   * journey's "Why we ask" renders in a question's header, BEFORE the answers in
   * the DOM, and a single-select answer submits on Enter or Space. Without this,
   * Tab from "Close" went straight to the first answer behind the scrim, and
   * Enter recorded a clinical answer (sex at birth, ethnicity) the patient could
   * not see they were choosing. `aria-modal` hides the page from a screen reader;
   * it does not stop the keyboard (WCAG 2.4.3, the APG modal dialog pattern).
   */
  trapFocus?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Escape reads the LATEST onCancel through a ref so the mount-only effect
  // never re-runs on a parent re-render (re-running would steal focus back to
  // the panel mid-typing in the reason field).
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  // Read through a ref for the same reason: the keydown listener is mount-only.
  const trapFocusRef = useRef(trapFocus);
  trapFocusRef.current = trapFocus;
  // The opener is captured DURING the first render — by effect time an
  // autofocused child (the reason field) has already taken focus, so reading
  // document.activeElement there would "return" focus to a removed node.
  const openerRef = useRef<HTMLElement | null>(null);
  if (openerRef.current === null) openerRef.current = document.activeElement as HTMLElement | null;

  useEffect(() => {
    if (!panelRef.current?.contains(document.activeElement)) panelRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancelRef.current();
      else if (trapFocusRef.current && panelRef.current) containTab(event, panelRef.current);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      openerRef.current?.focus?.();
    };
  }, []);

  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(19, 35, 27, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 'var(--gps-s-4)' }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={className}
        onClick={(event) => event.stopPropagation()}
        // A MODAL OWNS ITS OWN TYPOGRAPHY — it must never inherit the opener's
        // (2026-09-04). The panel is a DOM descendant of whatever rendered the
        // caller, and `position: fixed` does NOT break CSS inheritance, so the
        // copy took its alignment and weight from a mount site the reader
        // cannot see. `PlanLinkActions` sits in a `sections.tsx` `Row` value
        // span (`text-align: right; font-weight: 600`), so the "Re-send the
        // plan link?" dialog rendered four lines of right-aligned semibold
        // prose above a left-aligned button row — while the SAME dialog opened
        // from the Questionnaire tab read normally. Stating both here is what
        // makes one dialog look like one dialog wherever it is opened from.
        // 20px inset matches the estate's surface grammar (`kit.tsx` Card);
        // --gps-s-4 (16px) was a lone 4px-tighter exception.
        style={{ width, maxHeight: '90vh', overflowY: 'auto', background: 'var(--gps-surface)', borderRadius: 'var(--gps-r-lg)', padding: '20px', boxShadow: 'var(--gps-shadow)', textAlign: 'left', fontWeight: 400 }}
      >
        <h3 id={titleId} style={{ margin: '0 0 var(--gps-s-3)', fontSize: '17px', color: 'var(--gps-text)' }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

/**
 * The kit-grade confirmation dialog. `tone` is the INTENT of the confirm, and
 * it is the only thing that may reach for an alarm colour: `danger` paints it
 * --gps-danger-strong red (rejects, refunds, deletes, cancelling a plan,
 * prescribing-identity changes), and the default paints it the kit's
 * affirmative green for everything benign and reversible — re-sending an email,
 * moving a card, marking a test record, opening a password-reset. Pick `danger`
 * on the consequence, never on how important the action feels: the red only
 * means "this one is different" for as long as the benign dialogs are not
 * wearing it. `requireReason` gates
 * confirm behind a ≥3-trimmed-char reason; `optionalReason` shows the same
 * field ungated (an empty reason confirms with `undefined`). `busy` disables
 * both actions while the caller's async work runs.
 *
 * `confirmDisabled` ANDs the caller's own gate onto the reason gate, for the
 * flows whose `body` carries extra required fields the dialog cannot know about
 * (the re-subscribe lane-A override: an authorisation method, when it was taken
 * and an explicit attestation). It is deliberately additive — an unset value
 * behaves exactly as before, and the reason gate is never weakened by it.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  tone = 'default',
  confirmLabel,
  busy = false,
  requireReason = false,
  optionalReason = false,
  confirmDisabled = false,
  reasonLabel = 'Reason',
  reasonMaxLength,
  confirmDescribedBy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  tone?: 'default' | 'danger';
  confirmLabel: string;
  busy?: boolean;
  requireReason?: boolean;
  optionalReason?: boolean;
  /** Caller-owned extra gate, ANDed with the reason gate. */
  confirmDisabled?: boolean;
  reasonLabel?: string;
  /**
   * The server's ceiling on the reason, where it has one (additive, 2026-09-11 —
   * the marketing-consent withdrawal caps it at 500). The field stops at it
   * instead of letting someone type an essay the server will 400. Unset = no
   * cap, exactly as before.
   */
  reasonMaxLength?: number;
  /** The id of a caller-rendered error the confirm button should be described by. */
  confirmDescribedBy?: string;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const reasonId = useId();

  // A re-opened dialog must never carry the previous decision's reason.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!open) return null;

  const hasReasonField = requireReason || optionalReason;
  const trimmed = reason.trim();
  const blocked = busy || confirmDisabled || (requireReason && trimmed.length < 3);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (blocked) return;
    onConfirm(hasReasonField ? trimmed || undefined : undefined);
  }

  return (
    <DialogShell title={title} onCancel={busy ? () => {} : onCancel}>
      <form onSubmit={handleSubmit} aria-label={title}>
        {body ? <div style={{ color: 'var(--gps-text)', fontSize: '14.5px', margin: '0 0 var(--gps-s-3)' }}>{body}</div> : null}
        {hasReasonField ? (
          <Field
            label={reasonLabel}
            htmlFor={reasonId}
            hint={
              requireReason
                ? reasonMaxLength
                  ? `Required — at least 3 characters, up to ${reasonMaxLength}.`
                  : 'Required — at least 3 characters.'
                : 'Optional.'
            }
          >
            {/* autoFocus lands the caret straight in the reason box; DialogShell
                sees the focus already inside the panel and leaves it alone. */}
            <TextArea id={reasonId} autoFocus value={reason} maxLength={reasonMaxLength} onChange={(e) => setReason(e.target.value)} />
          </Field>
        ) : null}
        {/* flex-start is the flex default, but it is stated because it is now a
            DECISION: the copy above is explicitly left-aligned, and the two
            have to agree. It also matches the hand-rolled action rows in
            LeaveConfirmDialog and SinglePageForm, which wrap the same shell. */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
          <Button
            type="submit"
            // THE RED IS THE TONE'S, NOT EVERY DIALOG'S (2026-09-04). This was
            // hard-coded `cta` — terracotta (#c0492c), which kit.tsx reserves
            // for "the single conversion action per screen" and which sits one
            // shade off --gps-danger-strong (#b3261e). So re-sending an email a
            // patient already has wore the same alarm colour as a refund, and
            // the colour that is supposed to make someone stop and read stopped
            // carrying information. `danger` renders EXACTLY as before; only
            // benign confirms move to the kit's affirmative green `primary`.
            variant={tone === 'danger' ? 'cta' : 'primary'}
            disabled={blocked}
            aria-describedby={confirmDescribedBy}
            style={{ ...dialogButtonStyle, ...(tone === 'danger' ? { background: 'var(--gps-danger-strong)' } : {}) }}
          >
            {confirmLabel}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={dialogButtonStyle}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
